'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Skeleton, Alert, Button as MuiButton, CircularProgress } from '@mui/material';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import {
  PageHeader,
  Tab,
  Tag,
  Avatar,
  ListItem,
  ScheduleCalendar,
  IconButton as DSIconButton,
} from '@miso/design-system';
import type { DayData, ScheduleMode } from '@miso/design-system';
import { useMyVisitCounselList } from '@/entities/visit-counsel';
import type { VisitCounselView } from '@/entities/visit-counsel';
import { orderApi } from '@/entities/order';
import { customerApi } from '@/entities/customer';
import { VisitCounselState } from '@/entities/visit-counsel/model/visit-counsel.types';
import { useMyPlannerWorkScheduleCalendarData } from '@/entities/planner/model/useMyPlannerWorkScheduleCalendarData';
import { formatScheduleTimeSingle } from '@/shared/lib/timeslot/scheduleTimeRangeFormatter';

dayjs.locale('ko');

/** CounselListPage 우하단 FAB(bottom 24px + xl 56px) 아래 '더 불러오기' 등이 가려지지 않도록 */
const SCROLL_BOTTOM_INSET_PX = 96;

// ── Icons ─────────────────────────────────────────────────────────────────────

function HomeIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="white" aria-hidden="true">
      <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
    </svg>
  );
}

function ChevronUpIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M18 15l-6-6-6 6"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SmsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12zM7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z" />
    </svg>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** 'YYYY-MM-DD' → '13일 월요일' (오늘이면 '오늘 화요일') */
function formatDayLabel(dateStr: string): string {
  const d = dayjs(dateStr);
  if (d.isSame(dayjs(), 'day')) return `오늘 ${d.format('dddd')}`;
  if (d.month() !== dayjs().month())
    return `${d.format('M')}월 ${d.format('D')}일 ${d.format('dddd')}`;
  return `${d.format('D')}일 ${d.format('dddd')}`;
}

function toMonthKey(dateStr: string): string {
  return dayjs(dateStr).format('YYYY-MM');
}

/** "다음 상담" — 오늘 이후 (포함) 가장 가까운 상담 */
function resolveNextCounselId(items: VisitCounselView[]): string | null {
  const now = dayjs();
  const future = items
    .filter((c) => {
      if (c.state === VisitCounselState.COMPLETED) return false;
      const at = dayjs(`${c.scheduleDate} ${c.scheduleTime}`);
      return at.isValid() && at.isAfter(now);
    })
    .sort(
      (a, b) =>
        dayjs(`${a.scheduleDate} ${a.scheduleTime}`).valueOf() -
        dayjs(`${b.scheduleDate} ${b.scheduleTime}`).valueOf()
    );
  return future[0]?.id ?? null;
}

/** ScheduleCalendar용 dayData 변환 (해당 월 분만) */
function buildCalendarDayData(
  scheduleData: Record<string, { regions: string[]; counsels: number }>,
  monthKey: string
): Record<number, DayData> {
  const result: Record<number, DayData> = {};
  for (const [dateStr, data] of Object.entries(scheduleData)) {
    if (!dateStr.startsWith(monthKey)) continue;
    if (data.regions.length === 0 || data.regions[0] === '-') continue;
    const dayNum = Number(dateStr.split('-')[2]);
    const description =
      data.regions.length === 1 ? data.regions[0] : `${data.regions[0]}+${data.regions.length - 1}`;
    result[dayNum] = {
      description,
      tag: data.counsels > 0 ? `${data.counsels}건` : undefined,
    };
  }
  return result;
}

// ── Sub-components ────────────────────────────────────────────────────────────

/**
 * 클릭 시 상담 상세와 동일한 방식으로 전화번호를 조회한 뒤 sms: 링크를 여는 버튼.
 * 우선순위: customer.phone → order.ordererPhone
 */
function SmsButton({ orderId }: { orderId: string }) {
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (loading) return;
      setLoading(true);
      try {
        const order = await orderApi.getOrder(orderId);

        let phone = order.ordererPhone ?? '';

        if (order.ordererMisoCustomerId) {
          try {
            const customer = await customerApi.getCustomerByMisoId(order.ordererMisoCustomerId);
            if (customer.phone) phone = customer.phone;
          } catch {
            // customer 조회 실패 시 ordererPhone 사용
          }
        }

        if (phone) {
          window.location.href = `sms:${phone}`;
        } else {
          alert('전화번호 정보가 없습니다.');
        }
      } catch {
        alert('전화번호를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    },
    [orderId, loading]
  );

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label="문자 발송"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        borderRadius: '50%',
        border: 'none',
        background: 'transparent',
        color: '#1976d2',
        cursor: 'pointer',
        flexShrink: 0,
        padding: 0,
      }}
    >
      {loading ? <CircularProgress size={20} color="inherit" /> : <SmsIcon />}
    </button>
  );
}

function NextBadge() {
  return (
    <Tag variant="info" content="text">
      다음 상담
    </Tag>
  );
}

function DayHeader({ dateKey, count }: { dateKey: string; count: number }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        color: '#999',
        letterSpacing: '-0.03em',
        lineHeight: 1,
      }}
    >
      {formatDayLabel(dateKey)}
      {' · '}
      <span style={{ fontWeight: 500 }}>총 {count}건</span>
    </div>
  );
}

function CounselListSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '16px 24px 32px' }}>
      {[0, 1, 2, 3].map((i) => (
        <Box key={i} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton width={120} height={14} />
          {[0, 1].map((j) => (
            <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Skeleton variant="circular" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton width="60%" height={22} />
                <Skeleton width="80%" height={16} sx={{ mt: 0.5 }} />
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface PlannerCounselSelection {
  visitCounselId: string;
  scheduleDate: string;
}

export interface PlannerCounselListViewProps {
  /** 상담 카드 클릭 시 호출 */
  onSelectCounsel: (selection: PlannerCounselSelection) => void;
  /** 뒤로가기 (상담지도로 이동 등) — 있는 경우 PageHeader 좌측 버튼 활성화 */
  onBack?: () => void;
  /** plannerId — 서버 자동 주입이 안될 때 명시 */
  plannerId?: string;
  /** 활성화 여부 (auth/role guard 통과 후 true) */
  enabled?: boolean;
}

// ── Main view ─────────────────────────────────────────────────────────────────

/**
 * 플래너 전용 상담 목록 신규 UI.
 *
 * - 진행필요 탭: scheduleDate >= 오늘 이면서 COMPLETED 아닌 상담 (서버 날짜 필터 + 클라이언트 상태 필터)
 * - 모두보기 탭: 전체 상담 (날짜/상태 제한 없음)
 * - sticky ScheduleCalendar — 해당 월 근무지역(description) + 상담 수(tag).
 * - 날짜 선택 시 해당 day group으로 스크롤.
 */
export function PlannerCounselListView({
  onSelectCounsel,
  onBack,
  plannerId,
  enabled = true,
}: PlannerCounselListViewProps) {
  const [activeTab, setActiveTab] = useState<'pending' | 'all'>('pending');
  const [calendarMode, setCalendarMode] = useState<ScheduleMode>('week');
  const todayStr = useMemo(() => dayjs().format('YYYY-MM-DD'), []);
  const [calendarDate, setCalendarDate] = useState(todayStr);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // 진행필요: 오늘 이후 상담만 서버에서 필터링 (scheduleDate ASC — 오늘부터)
  // ASC 로 받아야 첫 페이지가 오늘에 가까운 날짜가 되고, '더 불러오기'가 미래로 확장된다.
  const pendingList = useMyVisitCounselList('asc', enabled, plannerId, todayStr);
  // 모두보기: 전체 상담 (scheduleDate DESC — 미래 먼저, 기존 유지)
  const allList = useMyVisitCounselList('desc', enabled, plannerId);

  const {
    items: rawItems,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    loadMore,
  } = activeTab === 'pending' ? pendingList : allList;

  // 현재 캘린더가 가리키는 월의 근무 데이터 (지역/상담 수)
  const monthKey = useMemo(() => toMonthKey(calendarDate), [calendarDate]);
  const { scheduleData } = useMyPlannerWorkScheduleCalendarData({ month: monthKey, enabled });

  const dayData = useMemo(
    () => buildCalendarDayData(scheduleData, monthKey),
    [scheduleData, monthKey]
  );

  // ── 필터링 + 그룹핑 ─────────────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    if (activeTab === 'all') return rawItems;
    // 진행필요: 서버 필터(scheduleDate >= 오늘) + 클라이언트 필터(COMPLETED 제외)
    return rawItems.filter((c) => c.state !== VisitCounselState.COMPLETED);
  }, [rawItems, activeTab]);

  const grouped = useMemo(() => {
    // 진행필요: 날짜·시간 오름차순(오늘 → 미래, 같은 날은 이른 시간부터) — 오늘 것이 맨 위.
    // 모두보기: 기존대로 날짜·시간 내림차순(미래 → 과거).
    const ascending = activeTab === 'pending';
    const map = new Map<string, VisitCounselView[]>();
    for (const c of filteredItems) {
      if (!map.has(c.scheduleDate)) map.set(c.scheduleDate, []);
      map.get(c.scheduleDate)!.push(c);
    }
    const sortedDates = [...map.keys()].sort((a, b) =>
      ascending ? a.localeCompare(b) : b.localeCompare(a)
    );
    const result: { dateKey: string; items: VisitCounselView[] }[] = [];
    for (const date of sortedDates) {
      const sorted = (map.get(date) ?? []).sort((a, b) =>
        ascending
          ? a.scheduleTime.localeCompare(b.scheduleTime)
          : b.scheduleTime.localeCompare(a.scheduleTime)
      );
      result.push({ dateKey: date, items: sorted });
    }
    return result;
  }, [filteredItems, activeTab]);

  const nextId = useMemo(() => resolveNextCounselId(rawItems), [rawItems]);

  // ── 스크롤 동작 ─────────────────────────────────────────────────────────
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrolledToTodayRef = useRef(false);

  const handleScroll = useCallback(() => {
    const top = scrollContainerRef.current?.scrollTop ?? 0;
    setShowScrollTop(top > 200);
  }, []);

  /** 날짜 선택 → 캘린더 날짜 업데이트 + 해당 섹션으로 스크롤 */
  const handleDateSelect = useCallback((dateId: string) => {
    setCalendarDate(dateId);
    requestAnimationFrame(() => {
      const section = sectionRefs.current.get(dateId);
      const container = scrollContainerRef.current;
      if (!section || !container) return;
      const containerRect = container.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      const offset = sectionRect.top - containerRect.top + container.scrollTop - 12;
      container.scrollTo({ top: offset, behavior: 'smooth' });
    });
  }, []);

  /** 데이터 첫 로드 후 오늘 그룹으로 자동 스크롤 (최초 1회) */
  useEffect(() => {
    if (isLoading || grouped.length === 0 || scrolledToTodayRef.current) return;
    const todayGroup = grouped.find((g) => g.dateKey === todayStr);
    const target = todayGroup?.dateKey ?? grouped[grouped.length - 1]?.dateKey;
    if (!target) return;
    requestAnimationFrame(() => {
      const section = sectionRefs.current.get(target);
      const container = scrollContainerRef.current;
      if (!section || !container) return;
      const containerRect = container.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();
      const offset = sectionRect.top - containerRect.top + container.scrollTop - 12;
      container.scrollTo({ top: offset, behavior: 'auto' });
      scrolledToTodayRef.current = true;
    });
  }, [isLoading, grouped, todayStr]);

  // ── 렌더링 ─────────────────────────────────────────────────────────────

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* 고정 헤더 */}
      <PageHeader title="상담 목록" onBack={onBack} backLabel="뒤로가기" />

      <Tab
        variant="underline"
        fullWidth
        size="l"
        items={[
          { id: 'pending', label: '진행필요' },
          { id: 'all', label: '모두보기' },
        ]}
        value={activeTab}
        onChange={(id) => setActiveTab(id as 'pending' | 'all')}
      />

      {/* 스크롤 영역 */}
      <Box
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          pb: `${SCROLL_BOTTOM_INSET_PX}px`,
        }}
      >
        {/* Sticky 캘린더 */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: '#ffffff',
            padding: '8px 8px',
          }}
        >
          <ScheduleCalendar
            mode={calendarMode}
            date={calendarDate}
            dayData={dayData}
            onDatePress={(entry) => handleDateSelect(String(entry.id))}
            onToggleMode={setCalendarMode}
          />
        </Box>

        {/* 본문 */}
        {error ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Alert severity="error">{error.message}</Alert>
          </Box>
        ) : isLoading && rawItems.length === 0 ? (
          <CounselListSkeleton />
        ) : grouped.length === 0 ? (
          <Box sx={{ px: 2, py: 4 }}>
            <Alert severity="info">
              {activeTab === 'pending' ? '진행필요한 상담이 없습니다.' : '배정된 상담이 없습니다.'}
            </Alert>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              padding: '8px 24px 32px',
            }}
          >
            {grouped.map((group) => (
              <Box
                key={group.dateKey}
                ref={(el) => {
                  if (el) sectionRefs.current.set(group.dateKey, el as HTMLDivElement);
                  else sectionRefs.current.delete(group.dateKey);
                }}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <DayHeader dateKey={group.dateKey} count={group.items.length} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                  {group.items.map((c) => (
                    <ListItem
                      key={c.id}
                      onClick={() =>
                        onSelectCounsel({
                          visitCounselId: c.id,
                          scheduleDate: c.scheduleDate,
                        })
                      }
                      leading={
                        <Avatar variant="green" size="md" aria-label="가정방문">
                          <HomeIcon />
                        </Avatar>
                      }
                      title={
                        <>
                          <span
                            style={{
                              fontSize: 20,
                              fontWeight: 500,
                              letterSpacing: '-0.03em',
                              color: '#000',
                              lineHeight: 1.2,
                            }}
                          >
                            {formatScheduleTimeSingle(c.scheduleTime)}
                          </span>
                          {c.id === nextId && <NextBadge />}
                        </>
                      }
                      description={
                        <span
                          style={{
                            display: 'flex',
                            gap: 4,
                            color: '#4d4d4d',
                            fontSize: 14,
                            letterSpacing: '-0.03em',
                            overflow: 'hidden',
                          }}
                        >
                          <span
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: '0 1 auto',
                            }}
                          >
                            {c.location}
                          </span>
                          <span style={{ flexShrink: 0 }}>·</span>
                          <span style={{ flexShrink: 0 }}>{c.supplyArea}평</span>
                        </span>
                      }
                      trailing={<SmsButton orderId={c.orderId} />}
                    />
                  ))}
                </Box>
              </Box>
            ))}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2, pb: 4 }}>
                <MuiButton
                  variant="outlined"
                  size="medium"
                  disabled={isLoadingMore}
                  onClick={loadMore}
                  sx={{ minHeight: 44 }}
                >
                  {isLoadingMore ? '불러오는 중...' : '더 불러오기'}
                </MuiButton>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* 맨위로 플로팅 버튼 */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 24,
          left: 16,
          zIndex: 20,
          opacity: showScrollTop ? 1 : 0,
          transform: showScrollTop ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 0.2s ease, transform 0.2s ease',
          pointerEvents: showScrollTop ? 'auto' : 'none',
        }}
      >
        <DSIconButton
          variant="primary"
          size="l"
          icon={<ChevronUpIcon />}
          aria-label="맨 위로 이동"
          onPress={() => scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
        />
      </Box>
    </Box>
  );
}
